import * as React from 'react';

import { useQueryClient, MutationStatus } from '@tanstack/react-query';

import {
  AllowedActions,
  AuthorizationRequestType,
  EventReport,
  Incident,
  IncidentDTO,
  ReportType,
} from '../api/clients';

import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorOverlay from '../../components/ErrorOverlay';

import useUpdateIncident from '../api/hooks/incident/useUpdateIncident';
import useGetIncidentReport from '../api/hooks/incident/useGetIncidentReport';
import useGetAllowedActionsForIncidents from '../api/hooks/common/useGetActionsForIncidents';
import useDeleteIncident from '../api/hooks/incident/useDeleteIncident';
import useGetFormColor from '../api/hooks/common/useGetFormColor';
import useCloseIncident from '../api/hooks/incident/useCloseIncident';
import useAddToIncident from '../api/hooks/event-report/useAddToIncident';
import useRemoveFromIncident from '../api/hooks/event-report/useRemoveFromIncident';

import queryKeys from '../api/queryKeys';
import useGetReportColor from '../api/hooks/common/useGetReportColor';
import useTitle from '../api/hooks/common/useTitle';

type IIncidentReportData = {
  incidentReport: Incident;
  allowedActions: AllowedActions | undefined;
  update: (
    data: Incident,
    successCallback?: any,
    failCallback?: any
  ) => void;
  closeIncident: (data: Incident,
    successCallback?: any,
    failCallback?: any) => void,
  addToIncident: (incident: Incident, eventReport: EventReport) => void,
  removeFromIncident: (incident: Incident, eventReport: EventReport) => void,
  addToIncidentMutationStatus: MutationStatus;
  removeFromIncidentMutationStatus: MutationStatus;
  updateStatus: MutationStatus;
  deleteIncident: (successCallback?: any) => void;
  getFormColor: (reportType: ReportType) => string;
  refetch: () => void;
};

const IncidentReportDataContext = React.createContext<IIncidentReportData>(undefined!);

export function IncidentReportDataProvider({
  children,
  incidentReportId,
  role,
}: {
  children: React.ReactNode,
  incidentReportId: number,
  role?: AuthorizationRequestType,
}) {
  const queryClient = useQueryClient();
  useTitle(document.documentURI);
  // eslint-disable-next-line max-len
  const { data: reportData, status: reportStatus, error: reportError, refetch } = useGetIncidentReport(incidentReportId, role || AuthorizationRequestType.Contribute);
  const { data: allowedActions } = useGetAllowedActionsForIncidents(reportData?.status, ReportType.Incident);
  const { status: updateMutationStatus, mutate: updateMutation } = useUpdateIncident();
  const { status: deleteMutationStatus, mutate: deleteMutation } = useDeleteIncident();
  const { status: completeMutationStatus, mutate: closeIncidentMutation } = useCloseIncident();
  const { status: addToIncidentMutationStatus, mutate: addToIncidentMutation } = useAddToIncident();
  const { status: removeFromIncidentMutationStatus, mutate: removeFromIncidentMutation } = useRemoveFromIncident();

  const { getFormColor } = useGetReportColor();

  const onSuccessfulMutation = (returnData: IncidentDTO, successCallback?: any, failCallback?: any) => {
    const { eventReport, error } = returnData;

    queryClient.setQueryData([queryKeys.Incident.Get, { id: eventReport?.id }], eventReport);
    if (!error) {
      if (successCallback) {
        successCallback(eventReport);
      }
    } else if (failCallback) failCallback(error);
  };

  const incidentReportData = React.useMemo<IIncidentReportData>(() => ({
    incidentReport: reportData!,
    allowedActions: allowedActions!,
    updateStatus: updateMutationStatus || completeMutationStatus || deleteMutationStatus,
    update: (data: Incident, successCallback?: any, failCallback?: any) => {
      updateMutation(data, {
        onSuccess: (returnData) => onSuccessfulMutation(
          returnData,
          successCallback,
          failCallback,
        ),
      });
    },
    closeIncident: (data: Incident, successCallback?: any, failCallback?: any) => {
      closeIncidentMutation(data, {
        onSuccess: (returnData) => onSuccessfulMutation(
          returnData,
          successCallback,
          failCallback,
        ),
      });
    },
    deleteIncident: (successCallback) => {
      deleteMutation(reportData?.id!, { onSuccess: (result: boolean) => {
        if (result) {
          successCallback();
        }
      } });
    },
    addToIncident: (incident: Incident, eventReport: EventReport) => {
      addToIncidentMutation({
        incidentId: incident.id,
        eventReportId: eventReport.id,
        incidentConcurrencyId: incident.concurrencyId,
        eventReportConcurrencyId: eventReport.concurrencyId,
      }, {
        onSuccess: () => {
          refetch();
        },
      });
    },
    removeFromIncident: (incident: Incident, eventReport: EventReport) => {
      removeFromIncidentMutation({
        incidentId: incident.id,
        eventReportId: eventReport.id,
        incidentConcurrencyId: incident.concurrencyId,
        eventReportConcurrencyId: eventReport.concurrencyId,
      }, {
        onSuccess: () => {
          refetch();
        },
      });
    },
    addToIncidentMutationStatus,
    removeFromIncidentMutationStatus,
    getFormColor,
    refetch,
  }), [
    reportData,
    allowedActions,
    updateMutationStatus,
    addToIncidentMutationStatus,
    removeFromIncidentMutationStatus,
  ]);

  if (reportStatus === 'pending') {
    return (
      <LoadingOverlay />
    );
  }

  if (reportStatus === 'error') {
    return (
      <ErrorOverlay
        title={reportError?.title}
        message={`An error occurred retrieving data for form: ${incidentReportId}`}
      />
    );
  }

  if (reportStatus === 'success' && reportData !== undefined) {
    return (
      <IncidentReportDataContext.Provider value={incidentReportData}>
        {children}
      </IncidentReportDataContext.Provider>
    );
  }

  return null;
}

export const useIncidentReportData = () => React.useContext(IncidentReportDataContext);
